// ═════════════════════════════════════════════════════════════════════════════
//  FIELD NAV — pure boot-decision helper (project-first nav, Task 4)
// ═════════════════════════════════════════════════════════════════════════════
// The field app's home used to be the Today board with an inline project
// picker; now the app opens on a list of the foreman's jobs and picks the job
// ONCE (Task 5 scopes the board itself to the picked job — this task only
// decides which screen boot lands on). Everything DOM/localStorage-dependent
// (reading window.projectRows, persisting to `ordinexFieldProject:<uid>`,
// painting the list, toggling screens) stays in js/app.js, a classic script
// tied to index.html's own ids and globals (sbClient, state, ...) that don't
// exist outside a logged-in field session. This file holds only the part that
// is a pure function of its arguments: given the crew's filable jobs and
// whatever context was cached from a previous visit, which screen does boot
// land on. Unit-tested in test/unit/field-nav.test.mjs without a DOM.
//
// app.js consumes this through window.Ordinex.fieldNav (js/core/index.js),
// the same convention every other core module already uses for the legacy
// classic-script pages — mirrors js/core/job-workspace.js's
// resolveJobContext for the admin side of this same feature.

// Decides the boot screen from the crew's filable jobs (already run through
// app.js's filterFilableProjects — a mother project with buildings is never
// itself pickable) plus whatever `{id, name}` was cached in localStorage from
// a previous visit.
//
//   mode: 'home'  — zero filable jobs; nothing to pick, land on the Today
//                   board exactly as before this feature (its own selector,
//                   unchanged — see js/app.js's showHomeDashboard()).
//   mode: 'enter' — a job to auto-enter: either the restored context matched
//                   a real job, or exactly one filable job exists (spec: "a
//                   foreman with exactly ONE active job lands straight in
//                   it"). `project` is always a REAL row from `filable`,
//                   never the stale cached one — the caller can never open
//                   the wrong job.
//   mode: 'list'  — 2+ filable jobs and no valid restored context: show the
//                   picker and wait for a tap.
//
// `stale` is true whenever a context WAS cached but didn't match a current
// filable job (the crew was unassigned, or the job was archived, since the
// last visit) — the caller uses this to know whether to wipe the stale
// localStorage entry. Per spec: never auto-pick the wrong job, never a dead
// end — a stale context always falls through to 'list', or, if only one job
// remains, to 'enter' on THAT job (never the one that was cached).
export function resolveFieldBootContext(filable, stored) {
  const rows = Array.isArray(filable) ? filable.filter(Boolean) : [];
  if (!rows.length) return { mode: 'home', project: null, stale: false };

  const match = (stored && stored.id) ? rows.find(p => p.id === stored.id) : null;
  if (match) return { mode: 'enter', project: match, stale: false };

  const stale = !!(stored && stored.id);
  if (rows.length === 1) return { mode: 'enter', project: rows[0], stale };
  return { mode: 'list', project: null, stale };
}
